import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { nx, NX_RING_CLIP, type NxSize } from "../lib/onyx-material";

/**
 * NotchedInput — a text field in the hex-notch silhouette, the form companion to
 * KeycapButton. The notch is a `clip-path`, so the field's border can't be a real
 * CSS `border` (it would be sliced at the notch — #158): the outline is an
 * `nx-rim` RING overlay that traces the notch. The ring is the focus affordance —
 * `--border` (hairline) at rest, `--ring` (accent) on focus, `--destructive` on
 * `aria-invalid` — and an accent drop-shadow glow (which survives the clip) lifts
 * the field on focus. `ref` forwards to the inner `<input>` so form libraries
 * register it directly.
 */
const fieldVariants = cva(
  [
    "relative flex w-full items-center gap-2 bg-background text-foreground",
    "transition-[filter] duration-180 ease-smooth-out",
    // Accent focus glow — a drop-shadow survives clip-path where a box-shadow ring
    // would be sliced at the notch. Destructive when the field is invalid.
    "focus-within:[filter:drop-shadow(0_0_3px_hsl(var(--ring)/0.45))]",
    "has-[input[aria-invalid=true]]:focus-within:[filter:drop-shadow(0_0_3px_hsl(var(--destructive)/0.45))]",
    "has-[input:disabled]:opacity-50",
  ].join(" "),
  {
    variants: {
      size: {
        sm: "h-9 px-3 text-xs",
        default: "h-10 px-3 text-sm",
        lg: "h-12 px-4 text-base",
      },
    },
    defaultVariants: { size: "default" },
  },
);

export interface NotchedInputProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof fieldVariants> {
  /** Notch chamfer size. Default `b` (refined for a control). */
  notch?: NxSize;
  /** Optional leading glyph rendered inside the field, before the input. */
  leadingIcon?: React.ReactNode;
  /** Class for the outer (clipped) field box; `className` lands on the `<input>`. */
  wrapperClassName?: string;
}

export const NotchedInput = React.forwardRef<HTMLInputElement, NotchedInputProps>(
  ({ className, wrapperClassName, notch = "b", size, leadingIcon, type, ...props }, ref) => (
    <div className={cn(nx[notch], fieldVariants({ size }), wrapperClassName)}>
      {leadingIcon != null ? (
        <span
          aria-hidden
          className="flex shrink-0 items-center text-muted-foreground [&_svg]:size-4"
        >
          {leadingIcon}
        </span>
      ) : null}
      <input
        ref={ref}
        type={type}
        className={cn(
          // `peer` so the ring sibling can react to :focus; the field box owns the
          // fill + clip, the input is transparent and borderless.
          "peer h-full w-full border-0 bg-transparent text-foreground outline-none",
          "placeholder:text-muted-foreground disabled:cursor-not-allowed",
          className,
        )}
        {...props}
      />
      {/* The notched keyline ring — traces the notch where a real border is clipped.
          Hairline at rest → accent on focus → destructive when invalid. */}
      <span
        aria-hidden
        className={cn(
          "nx-rim pointer-events-none absolute inset-0",
          "[--nx-rim:hsl(var(--border))]",
          // Eased like the field's glow (and the Button/Keycap rims, ui#229
          // pair): the flip used to snap while the filter eased.
          "transition-colors duration-180 ease-smooth-out",
          "peer-focus:[--nx-rim:hsl(var(--ring))]",
          "peer-aria-[invalid=true]:[--nx-rim:hsl(var(--destructive))]",
        )}
        style={{ clipPath: NX_RING_CLIP[notch] }}
      />
    </div>
  ),
);

NotchedInput.displayName = "NotchedInput";
